import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { PubBadge } from '../components/PubBadge'
import { api, type Segment } from '../api/client'
import { fmtInt, publicationColor } from '../lib/format'

// Canonical slug order for per-title sections.
const TITLE_ORDER: string[] = [
  'rc_car_action',
  'flight_journal',
  'model_airplane_news',
  'diecast',
]

const TITLE_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

const CHILD_SUFFIX_WITH = '-with-email'
const CHILD_SUFFIX_NO = '-no-email'

type ChildMap = Record<string, { withEmail?: Segment; noEmail?: Segment }>

type Category = 'active' | 'expiring' | 'lapsed' | 'other'
const CATEGORY_ORDER: Category[] = ['active', 'expiring', 'lapsed', 'other']
const CATEGORY_LABEL: Record<Category, string> = {
  active: 'Active',
  expiring: 'Pre-expiration',
  lapsed: 'Post-expiration',
  other: 'Custom',
}

function categorize(slug: string): Category {
  if (slug.startsWith('active_') || slug.startsWith('active-')) return 'active'
  if (slug.startsWith('expiring-') || slug.startsWith('expiring_')) return 'expiring'
  if (slug.startsWith('lapsed-') || slug.startsWith('lapsed_')) return 'lapsed'
  return 'other'
}

function categorySortKey(slug: string): number {
  // Three patterns to handle:
  //   (a) `-mo-N` — current rolling segments. Key = N (0 = current
  //       month, 1 = next, ...). Today + N months.
  //   (b) `-YYYY-MM` — historical cohorts. Key = months relative to
  //       today (negative). Sept 2025 from May 2026 = -8.
  //   (c) `-Nmo` — legacy lifecycle slugs. Key = N.
  // Sorting ascending gives chronological: oldest historical first,
  // then current month (mo-0), then forecast months.
  const moMatch = slug.match(/-mo-(\d+)$/)
  if (moMatch) return Number(moMatch[1])
  const ymMatch = slug.match(/-(\d{4})-(\d{2})$/)
  if (ymMatch) {
    const year = Number(ymMatch[1])
    const month = Number(ymMatch[2])
    const now = new Date()
    return (year - now.getFullYear()) * 12 + (month - 1 - now.getMonth())
  }
  const moLegacy = slug.match(/-(\d+)mo$/)
  if (moLegacy) return Number(moLegacy[1])
  return 0
}

function stripTitlePrefix(name: string, titleName: string): string {
  if (name.startsWith(`${titleName} · `)) return name.slice(titleName.length + 3)
  if (name.startsWith(`${titleName} `)) return name.slice(titleName.length + 1)
  return name
}

function isChildSegment(s: Segment): boolean {
  return s.slug.endsWith(CHILD_SUFFIX_WITH) || s.slug.endsWith(CHILD_SUFFIX_NO)
}

type TitleGroup = {
  key: string
  titleName: string | null   // null → cross-title / all-titles
  labelOverride: string | null
  color: string | null
  segments: Segment[]
}

type SegmentMode = 'all' | 'with-email' | 'without-email'

// Pre-/post-expiration lifecycle segments (slugs `expiring-*` /
// `lapsed-*`) are deprecated as of 2026-05-04 — disabled in DB to
// preserve campaign history but hidden from every UI view. The
// AR-Off-Email custom segments cover the same workflow more
// accurately.
function _isDeprecatedLifecycle(slug: string): boolean {
  return slug.startsWith('expiring-') || slug.startsWith('lapsed-')
}

function selectSegments(segments: Segment[], mode: SegmentMode): Segment[] {
  const live = segments.filter((s) => !_isDeprecatedLifecycle(s.slug))
  if (mode === 'with-email') {
    // AR Off Email custom segments only — the legacy
    // `active-*-with-email` totals already render on the parent
    // /segments view, so we exclude them from the sub-view to keep
    // it focused on actionable per-cohort renewal-email targets.
    return live.filter((s) => s.slug.startsWith('ar-off-email-'))
  }
  if (mode === 'without-email') {
    // Mirror the With Email view: AR Off No Email custom segments
    // only (one per title × month-offset). Active-*-no-email totals
    // are on the parent view.
    return live.filter((s) => s.slug.startsWith('ar-off-no-email-'))
  }
  // All-view: parents only (children appear in their own sub-views).
  return live.filter((s) => !isChildSegment(s))
}


function groupSegments(segments: Segment[]): TitleGroup[] {
  const bySlug: Record<string, Segment[]> = {}
  const crossTitle: Segment[] = []
  const allTitles: Segment[] = []

  for (const s of segments) {
    const pubs = s.target_publications ?? []
    if (pubs.length === 0) allTitles.push(s)
    else if (pubs.length === 1) (bySlug[pubs[0]] ??= []).push(s)
    else crossTitle.push(s)
  }

  const out: TitleGroup[] = []
  for (const slug of TITLE_ORDER) {
    const segs = bySlug[slug]
    if (!segs || segs.length === 0) continue
    out.push({
      key: slug,
      titleName: TITLE_NAMES[slug] ?? slug,
      labelOverride: null,
      color: publicationColor(slug),
      segments: segs,
    })
  }
  for (const slug of Object.keys(bySlug)) {
    if (TITLE_ORDER.includes(slug)) continue
    out.push({
      key: slug,
      titleName: slug,
      labelOverride: null,
      color: publicationColor(slug),
      segments: bySlug[slug],
    })
  }
  if (crossTitle.length > 0) {
    out.push({
      key: '__cross_title__',
      titleName: null,
      labelOverride: 'Cross-title',
      color: null,
      segments: crossTitle,
    })
  }
  if (allTitles.length > 0) {
    out.push({
      key: '__all_titles__',
      titleName: null,
      labelOverride: 'All titles · no publication filter',
      color: null,
      segments: allTitles,
    })
  }
  return out
}

const MODE_LABELS: Record<SegmentMode, { kicker: string; subtitle: string }> = {
  'all': {
    kicker: 'audience rules · totals',
    subtitle: 'Parent segments. Use the With Email / Without Email sub-views for email-presence-filtered children.',
  },
  'with-email': {
    kicker: 'with email · deliverable subset',
    subtitle: 'Parent segments narrowed to subscribers with a deliverable email on file. Renewal-email targets.',
  },
  'without-email': {
    kicker: 'without email · direct-mail subset',
    subtitle: 'Parent segments narrowed to subscribers without a deliverable email. Direct-mail or attrition targets.',
  },
}

export function SegmentsPage({ mode = 'all' }: { mode?: SegmentMode } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ items: Segment[] }>('/api/segments'),
  })
  const labels = MODE_LABELS[mode]

  // Sub-views (with-email / without-email) show the detailed list as
  // before. The 'all' view collapses to a 4-row by-title summary —
  // total / with-email / without-email per publication, no custom or
  // child segments cluttering the audit-level view.
  if (mode !== 'all') {
    const visible = data ? selectSegments(data.items, mode) : []
    const groups = data ? groupSegments(visible) : []
    return (
      <div className="p-8 space-y-8 max-w-[1400px]">
        <Header labels={labels} showNewButton={false} />
        {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
        {!isLoading && visible.length === 0 && (
          <div className="label text-paper-trace p-6">No segments in this view yet.</div>
        )}
        <div className="space-y-4">
          {groups.map((g) => (
            <TitleSection key={g.key} group={g} childMap={{}} mode={mode} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 space-y-8 max-w-[1400px]">
      <Header labels={labels} showNewButton />
      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
      {!isLoading && data && <SegmentsByTitleSummary segments={data.items} />}
    </div>
  )
}

function Header({
  labels, showNewButton,
}: {
  labels: { kicker: string; subtitle: string }
  showNewButton: boolean
}) {
  return (
    <header className="flex items-end justify-between border-b border-ink-500 pb-6">
      <div>
        <div className="label mb-1">Composition</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Segments
          <span className="text-amber-glow"> ∙ </span>
          <span className="italic font-light">{labels.kicker}</span>
        </h1>
        <div className="label text-paper-dim mt-1.5 normal-case tracking-normal font-mono text-[12px]">
          {labels.subtitle}
        </div>
      </div>
      {showNewButton && (
        <Link to="/segments/new">
          <Button size="lg">+ New Segment</Button>
        </Link>
      )}
    </header>
  )
}

function SegmentsByTitleSummary({ segments }: { segments: Segment[] }) {
  // For each publication, locate its baseline `active-{slug}` parent
  // plus its email-presence children (`-with-email` / `-no-email`).
  // Render exactly four rows — RCA / FJ / MAN / DCX — with Total,
  // With Email, Without Email columns. Custom (ar-off-email-*) and
  // disabled lifecycle segments are intentionally excluded; they
  // belong on the With Email / Without Email sub-views.
  const bySlug: Record<string, Segment> = {}
  for (const s of segments) bySlug[s.slug] = s

  const rows = TITLE_ORDER.map((pub) => {
    const parent = bySlug[`active-${pub}`] ?? bySlug[`active_${pub}`]
    const withEmail =
      bySlug[`active-${pub}-with-email`] ?? bySlug[`active_${pub}-with-email`]
    const noEmail =
      bySlug[`active-${pub}-no-email`] ?? bySlug[`active_${pub}-no-email`]
    return {
      pub,
      pubName: TITLE_NAMES[pub] ?? pub,
      total: parent?.member_count ?? 0,
      totalId: parent?.id,
      withEmailCount: withEmail?.member_count ?? 0,
      withEmailId: withEmail?.id,
      noEmailCount: noEmail?.member_count ?? 0,
      noEmailId: noEmail?.id,
    }
  })
  const grand = rows.reduce(
    (a, r) => ({
      total: a.total + r.total,
      withEmailCount: a.withEmailCount + r.withEmailCount,
      noEmailCount: a.noEmailCount + r.noEmailCount,
    }),
    { total: 0, withEmailCount: 0, noEmailCount: 0 },
  )

  return (
    <div className="border border-ink-500 bg-ink-800/40">
      <table className="w-full text-[13px] font-mono">
        <thead>
          <tr className="border-b border-ink-500/60 bg-ink-800/60">
            <th className="label py-2 px-4 font-normal text-left">Title</th>
            <th className="label py-2 px-3 font-normal text-right">Total</th>
            <th className="label py-2 px-3 font-normal text-right text-radar-cyan">With Email</th>
            <th className="label py-2 px-3 font-normal text-right text-radar-violet">Without Email</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.pub} className="border-b border-ink-500/30 hover:bg-ink-700/30">
              <td className="px-4 py-2.5">
                <PubBadge slug={r.pub} />
              </td>
              <td className="px-3 py-2.5 text-right">
                <SummaryCell count={r.total} segId={r.totalId} tone="amber" />
              </td>
              <td className="px-3 py-2.5 text-right">
                <SummaryCell count={r.withEmailCount} segId={r.withEmailId} tone="cyan" />
              </td>
              <td className="px-3 py-2.5 text-right">
                <SummaryCell count={r.noEmailCount} segId={r.noEmailId} tone="violet" />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-amber-glow/40 bg-ink-700/60">
            <td className="px-4 py-3 label-amber">Total · 4 titles</td>
            <td className="px-3 py-3 text-right font-mono font-semibold tabular-nums text-amber-glow text-[15px]">
              {fmtInt(grand.total)}
            </td>
            <td className="px-3 py-3 text-right font-mono font-semibold tabular-nums text-radar-cyan text-[15px]">
              {fmtInt(grand.withEmailCount)}
            </td>
            <td className="px-3 py-3 text-right font-mono font-semibold tabular-nums text-radar-violet text-[15px]">
              {fmtInt(grand.noEmailCount)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function SummaryCell({
  count, segId, tone,
}: {
  count: number
  segId?: number
  tone: 'amber' | 'cyan' | 'violet'
}) {
  const isEmpty = count === 0
  const toneClass = isEmpty
    ? 'text-paper-trace'
    : tone === 'amber'
      ? 'text-amber-glow font-semibold'
      : tone === 'cyan'
        ? 'text-radar-cyan'
        : 'text-radar-violet'
  if (segId === undefined) {
    return <span className={`font-mono tabular-nums ${toneClass}`}>{fmtInt(count)}</span>
  }
  return (
    <Link
      to={`/segments/${segId}`}
      className={`font-mono tabular-nums ${toneClass} hover:brightness-125`}
    >
      {fmtInt(count)}
    </Link>
  )
}

function TitleSection({
  group, childMap, mode,
}: {
  group: TitleGroup
  childMap: ChildMap
  mode: SegmentMode
}) {
  const memberTotal = group.segments.reduce((a, s) => a + (s.member_count ?? 0), 0)
  const activeCount = group.segments.find((s) => categorize(s.slug) === 'active')?.member_count ?? 0

  // Bucket by category
  const byCategory: Record<Category, Segment[]> = { active: [], expiring: [], lapsed: [], other: [] }
  for (const s of group.segments) byCategory[categorize(s.slug)].push(s)
  for (const cat of CATEGORY_ORDER) {
    byCategory[cat].sort((a, b) => categorySortKey(a.slug) - categorySortKey(b.slug))
  }

  const titleName = group.titleName ?? ''

  return (
    <details
      open
      className="group/section border border-ink-500 bg-ink-800/40"
    >
      <summary className="flex items-baseline justify-between px-4 py-3 cursor-pointer list-none select-none border-b border-ink-500/60 hover:bg-ink-700/30">
        <span className="flex items-center gap-3">
          {group.titleName && group.key !== '__cross_title__' && group.key !== '__all_titles__' ? (
            <PubBadge slug={group.key} />
          ) : (
            <span className="label-amber">{group.labelOverride}</span>
          )}
          <span className="label text-paper-trace">
            {group.segments.length} segment{group.segments.length === 1 ? '' : 's'}
            {activeCount > 0 && ` · ${fmtInt(activeCount)} active`}
            {memberTotal > activeCount && ` · ${fmtInt(memberTotal)} rows total`}
          </span>
        </span>
        <span className="label text-paper-trace group-open/section:rotate-90 transition-transform">
          ▸
        </span>
      </summary>

      <table className="w-full text-[13px] font-mono">
        <thead>
          <tr className="border-b border-ink-500/60 bg-ink-800/60">
            <th className="label py-2 px-4 font-normal text-left">Segment</th>
            <th className="label py-2 px-3 font-normal text-right">Total</th>
            {mode === 'all' && (
              <>
                <th className="label py-2 px-3 font-normal text-right text-radar-cyan">With Email</th>
                <th className="label py-2 px-3 font-normal text-right text-radar-violet">Without Email</th>
              </>
            )}
            <th className="label py-2 px-3 font-normal text-left w-[220px]">Mailgun list</th>
          </tr>
        </thead>
        <tbody>
          {CATEGORY_ORDER.map((cat) => {
            const segs = byCategory[cat]
            if (segs.length === 0) return null
            return (
              <CategoryBlock
                key={cat}
                category={cat}
                segments={segs}
                childMap={childMap}
                titleName={titleName}
                mode={mode}
              />
            )
          })}
        </tbody>
      </table>
    </details>
  )
}

function CategoryBlock({
  category, segments, childMap, titleName, mode,
}: {
  category: Category
  segments: Segment[]
  childMap: ChildMap
  titleName: string
  mode: SegmentMode
}) {
  const totalCols = mode === 'all' ? 5 : 3
  return (
    <>
      <tr className="bg-ink-700/40">
        <td colSpan={totalCols} className="label-amber px-4 py-1.5 border-t border-ink-500/40">
          {CATEGORY_LABEL[category]}
          <span className="text-paper-trace font-normal ml-2">
            · {segments.length} bucket{segments.length === 1 ? '' : 's'}
          </span>
        </td>
      </tr>
      {segments.map((s) => (
        <SegmentRow
          key={s.id}
          segment={s}
          child={childMap[s.slug]}
          category={category}
          titleName={titleName}
          mode={mode}
        />
      ))}
    </>
  )
}

function SegmentRow({
  segment, child, category, titleName, mode,
}: {
  segment: Segment
  child?: { withEmail?: Segment; noEmail?: Segment }
  category: Category
  titleName: string
  mode: SegmentMode
}) {
  const count = segment.member_count ?? 0
  const isEmpty = count === 0
  const rowTone = isEmpty ? 'text-paper-trace' : 'text-paper'
  const countTone = isEmpty
    ? 'text-paper-trace'
    : category === 'active'
      ? 'text-amber-glow'
      : 'text-paper'
  const displayName = stripTitlePrefix(segment.name, titleName)
  const bound = !!segment.mailgun_list_address

  // Strip the trailing "-with-email" / "-no-email" suffix from child
  // names rendered in the sub-views — the route header already states
  // the email-presence filter.
  const cleanName = displayName
    .replace(/ · With Email$/, '')
    .replace(/ · No Email$/, '')

  return (
    <tr
      className={`border-b border-ink-500/30 hover:bg-ink-700/30 ${isEmpty ? 'opacity-60' : ''}`}
    >
      <td className={`px-4 py-2 ${rowTone}`}>
        <Link to={`/segments/${segment.id}`} className="hover:text-amber-glow">
          {cleanName}
        </Link>
        {!segment.enabled && (
          <span className="ml-2 label text-radar-red">DISABLED</span>
        )}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums ${countTone} ${category === 'active' ? 'font-semibold text-[15px]' : ''}`}>
        {fmtInt(count)}
      </td>
      {mode === 'all' && (
        <>
          <ChildCountCell seg={child?.withEmail} fallbackParent={segment} fallbackKind="with" tone="cyan" />
          <ChildCountCell seg={child?.noEmail} fallbackParent={segment} fallbackKind="without" tone="violet" />
        </>
      )}
      <td className="px-3 py-2 text-paper-dim truncate max-w-[220px]">
        {bound ? (
          <span className="text-[11px]">{segment.mailgun_list_address}</span>
        ) : (
          <span className="text-paper-trace">—</span>
        )}
      </td>
    </tr>
  )
}

function ChildCountCell({
  seg, fallbackParent, fallbackKind, tone,
}: {
  seg?: Segment
  fallbackParent: Segment
  fallbackKind: 'with' | 'without'
  tone: 'cyan' | 'violet'
}) {
  // For parents that have an explicit `-with-email` / `-no-email`
  // child, use the child's count and link to its detail. For the
  // AR-Off-Email custom parents (which are inherently email-required
  // by their predicate), the With Email cell mirrors the parent's
  // total and the Without Email cell shows 0.
  let count: number
  let linkId: number
  if (seg) {
    count = seg.member_count ?? 0
    linkId = seg.id
  } else if (fallbackParent.slug.startsWith('ar-off-email-')) {
    count = fallbackKind === 'with' ? (fallbackParent.member_count ?? 0) : 0
    linkId = fallbackParent.id
  } else {
    return <td className="px-3 py-2 text-right text-paper-trace tabular-nums">—</td>
  }
  const isEmpty = count === 0
  const toneClass = isEmpty
    ? 'text-paper-trace'
    : tone === 'cyan'
      ? 'text-radar-cyan'
      : 'text-radar-violet'
  return (
    <td className={`px-3 py-2 text-right tabular-nums ${toneClass}`}>
      <Link
        to={`/segments/${linkId}`}
        className="hover:brightness-125"
        onClick={(e) => e.stopPropagation()}
      >
        {fmtInt(count)}
      </Link>
    </td>
  )
}
